/**
 * Generic helpers for coercing values from untyped payloads (BigQuery JSON,
 * `data_json` blobs, etc.) into nullable primitives or arrays. Use these
 * instead of redefining ad-hoc parsers inside components.
 */

export const isRecord = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

export const toNullableString = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
};

export const toNullableBoolean = (value: unknown): boolean | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const normalized = value.toLowerCase();
        if (normalized === 'true') return true;
        if (normalized === 'false') return false;
    }
    return null;
};

export const toNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
};

const parseFlexibleNumber = (value: string | number): number => {
    if (typeof value === 'number') {
        return value;
    }

    const sanitizedValue = value.trim().replace(/[^\d,.-]/g, '');
    if (!sanitizedValue) {
        return Number.NaN;
    }

    const dotMatches = sanitizedValue.match(/\./g) ?? [];
    const hasComma = sanitizedValue.includes(',');

    if (hasComma) {
        if (dotMatches.length > 0) {
            if (sanitizedValue.lastIndexOf(',') > sanitizedValue.lastIndexOf('.')) {
                return Number(sanitizedValue.replace(/\./g, '').replace(',', '.'));
            }

            return Number(sanitizedValue.replace(/,/g, ''));
        }

        return Number(sanitizedValue.replace(',', '.'));
    }

    if (dotMatches.length > 1) {
        return Number(sanitizedValue.replace(/\./g, ''));
    }

    if (dotMatches.length === 1) {
        const [integerPart = '', fractionalPart = ''] = sanitizedValue.split('.');
        if (integerPart.length <= 3 && fractionalPart.length === 3) {
            return Number(`${integerPart}${fractionalPart}`);
        }
    }

    return Number(sanitizedValue);
};

/**
 * Parses either standard decimal strings ("7856295271.55"), localized
 * Brazilian strings ("1.234,56"), or raw numbers without corrupting the
 * decimal separator.
 */
export const toFlexibleNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = parseFlexibleNumber(typeof value === 'string' || typeof value === 'number' ? value : String(value));
    return Number.isFinite(numberValue) ? numberValue : null;
};

/**
 * Like `toNullableNumber`, but tolerates Brazilian-formatted numeric strings
 * such as "1.234,56" by stripping thousands separators and converting the
 * decimal comma to a dot before parsing.
 */
export const toLocalizedNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const numberValue = typeof value === 'string'
        ? Number(value.replace(/\./g, '').replace(',', '.'))
        : Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
};

export const toStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) return [];
    return value
        .map((item) => toNullableString(item))
        .filter((item): item is string => Boolean(item));
};
